import React, { useState } from 'react';
import { Image, Calendar, Smile, Send, Paperclip, Sparkles, X } from 'lucide-react';
import { api } from '../../services/api';
import { User, Post } from '../../types';
import styles from './Post.module.css';

interface PostComposerProps {
  user: User | null;
  onPostCreated: (post: Post) => void;
  replyToId?: string;
  quoteId?: string;
  placeholder?: string;
}

export const PostComposer: React.FC<PostComposerProps> = ({
  user,
  onPostCreated,
  replyToId,
  quoteId,
  placeholder = "What's happening on Cordle?",
}) => {
  const [content, setContent] = useState('');
  const [mediaUrls, setMediaUrls] = useState<string[]>([]);
  const [mediaTypes, setMediaTypes] = useState<string[]>([]);
  const [scheduledAt, setScheduledAt] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showScheduler, setShowScheduler] = useState(false);

  if (!user) return null;

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const res = await api.uploadMedia(file);
        setMediaUrls((prev) => [...prev, res.url]);
        setMediaTypes((prev) => [...prev, res.type]);
      }
    } catch (err: any) {
      alert(err.message || 'Media upload failed');
    } finally {
      setUploading(false);
    }
  };

  const removeMedia = (index: number) => {
    setMediaUrls((prev) => prev.filter((_, i) => i !== index));
    setMediaTypes((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim() && mediaUrls.length === 0) return;

    setSubmitting(true);
    try {
      const res = await api.createPost({
        content,
        mediaUrls,
        mediaTypes,
        replyToId,
        quoteId,
        scheduledAt: scheduledAt ? new Date(scheduledAt).getTime() : null,
      });

      setContent('');
      setMediaUrls([]);
      setMediaTypes([]);
      setScheduledAt('');
      setShowScheduler(false);
      onPostCreated(res.post);
    } catch (err: any) {
      alert(err.message || 'Failed to submit post');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.composer}>
      <div className={styles.composerHeader}>
        <img src={user.avatar_url} alt={user.display_name} className={styles.avatar} />
        <div style={{ flex: 1 }}>
          <textarea
            className={styles.textarea}
            placeholder={placeholder}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            maxLength={500}
          />

          {/* Media Previews */}
          {mediaUrls.length > 0 && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', margin: '10px 0' }}>
              {mediaUrls.map((url, idx) => (
                <div key={idx} style={{ position: 'relative', width: '90px', height: '90px' }}>
                  <img
                    src={url}
                    alt="Upload preview"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }}
                  />
                  <button
                    onClick={() => removeMedia(idx)}
                    style={{
                      position: 'absolute',
                      top: '4px',
                      right: '4px',
                      background: 'rgba(0,0,0,0.7)',
                      color: 'white',
                      borderRadius: '50%',
                      padding: '2px',
                    }}
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Scheduler option */}
          {showScheduler && (
            <div style={{ margin: '10px 0' }}>
              <input
                type="datetime-local"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                style={{
                  padding: '6px 12px',
                  background: 'var(--bg-tertiary)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: '6px',
                  color: 'var(--text-primary)',
                }}
              />
            </div>
          )}

          <div className={styles.composerActions}>
            <div className={styles.toolbar}>
              <label className={styles.toolBtn} title="Upload Media (Images / Videos to Cloudflare R2)">
                <Image size={18} />
                <input type="file" accept="image/*,video/*" multiple onChange={handleFileUpload} style={{ display: 'none' }} />
              </label>

              <button
                type="button"
                className={styles.toolBtn}
                title="Schedule Post"
                onClick={() => setShowScheduler(!showScheduler)}
              >
                <Calendar size={18} />
              </button>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <span className={styles.charCount}>{content.length} / 500</span>
              <button
                type="button"
                className={styles.postBtn}
                onClick={handleSubmit}
                disabled={submitting || uploading || (!content.trim() && mediaUrls.length === 0)}
              >
                {submitting ? 'Posting...' : uploading ? 'Uploading...' : 'Post'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
